<template>
  <!--
    Overlay global de ligação. Fica no App.vue e não dentro do chat de propósito:
    uma ligação pode chegar de um número SEM contato e SEM atendimento aberto —
    justamente o caso que a feature existe para mostrar — então não há chat onde
    ancorar o card.

    LISTAS, não cards únicos: o WhatsApp entrega ofertas concorrentes para o
    mesmo número (verificado em 2026-09-03, 14 s de sobreposição no gateway).
  -->
  <!--
    MINIMIZADA (só no mobile): em tela cheia o card tapa o chat inteiro, então
    tem de haver uma saída que não seja encerrar a ligação. A faixa é a porta
    de volta — a chamada continua viva atrás dela.
  -->
  <button
    v-if="minimizada && quantidadeDeCards"
    type="button"
    class="ligacao-barra"
    @click="minimizada = false"
  >
    <i class="ri-phone-fill" aria-hidden="true"></i>
    <span class="ligacao-barra-texto">{{ rotuloBarra }}</span>
    <small v-if="tempoBarra" class="ligacao-barra-tempo">{{ tempoBarra }}</small>
  </button>

  <div
    v-else-if="tocando.length || ativas.length"
    ref="overlay"
    class="ligacao-overlay"
    :class="{
      'ligacao-overlay--movida': !!posicao && !isMobileViewport,
      'ligacao-overlay--arrastando': arrastando,
      'ligacao-overlay--mobile': isMobileViewport,
    }"
    :style="estiloOverlay"
    @pointerdown="iniciarArraste"
    @pointermove="arrastar"
    @pointerup="soltarArraste"
    @pointercancel="soltarArraste"
    @lostpointercapture="encerrarGesto"
    @dblclick="restaurarPosicao"
  >
    <!-- Recolher: a única saída sem encerrar a ligação. Vale nos dois layouts —
         no desktop o card fica por cima da conversa do mesmo jeito, e quem está
         em ligação precisa digitar. -->
    <div class="ligacao-cabecalho">
      <button
        type="button"
        class="ligacao-minimizar"
        title="Minimizar"
        aria-label="Minimizar ligação"
        @click="minimizarTelaCheia"
      >
        <i class="ri-arrow-down-s-line" aria-hidden="true"></i>
      </button>
    </div>

    <!-- TOCANDO -->
    <div
      v-for="chamada in tocando"
      :key="chamada.idLigacao"
      class="ligacao-card ligacao-card--tocando"
      :class="{ 'ligacao-card--solo': solo }"
    >
      <div
        class="ligacao-topo"
        :title="isMobileViewport ? null : 'Arraste para mover — duplo clique volta ao canto'"
      >
        <!-- Avatar do contato. `@error` derruba a URL para as iniciais: foto de
             perfil do WhatsApp expira, e um quadrado quebrado no card é pior
             que a inicial do nome. -->
        <img
          v-if="chamada.imagem"
          :src="chamada.imagem"
          class="ligacao-avatar"
          alt=""
          draggable="false"
          @error="chamada.imagem = null"
        />
        <span v-else class="ligacao-avatar ligacao-avatar--vazio" aria-hidden="true">
          {{ iniciais(chamada) }}
        </span>
        <div class="ligacao-info">
          <span class="ligacao-titulo">
            {{ chamada.ehVideo ? 'Chamada de vídeo' : 'Ligação' }} recebida
          </span>
          <strong class="ligacao-nome">{{ chamada.nome || chamada.codigo }}</strong>
        <!-- O número só aparece quando há nome, para não repetir a mesma string. -->
          <small v-if="chamada.nome" class="ligacao-codigo">{{ chamada.codigo }}</small>
          <!-- Em qual número a pessoa ligou. Operador com várias inboxes atende
               por empresas diferentes, e "quem é o cliente" não responde "de
               quem ele é cliente". -->
          <small v-if="nomeDaInbox(chamada)" class="ligacao-inbox">
            para {{ nomeDaInbox(chamada) }}
          </small>
          <small v-if="chamada.ehVideo" class="ligacao-aviso">
            Só o áudio é transmitido.
          </small>
          <!-- Conta as com ÁUDIO, não as da lista: desde que `ativas` passou a
               incluir ligação viva sem sessão de áudio, `ativas.length` inflava
               o número e prometia uma mistura de vozes que não pode acontecer. -->
          <small v-if="comAudio" class="ligacao-aviso">
            Já há {{ comAudio }} ligação(ões) com áudio ativo — as vozes vão se misturar.
          </small>
        </div>
      </div>
      <div class="ligacao-acoes">
        <!-- Ícone + rótulo nos dois: o CSS mostra o texto no desktop e o
             ícone no mobile, onde o botão é redondo. O rótulo continua no
             `aria-label`, então o leitor de tela não perde nada. -->
        <button
          class="ligacao-btn ligacao-btn--atender"
          title="Atender"
          aria-label="Atender"
          @click="atender(chamada.idLigacao)"
        >
          <i class="ri-phone-fill" aria-hidden="true"></i>
          <span class="ligacao-btn-rotulo">Atender</span>
        </button>
        <button
          class="ligacao-btn ligacao-btn--recusar"
          title="Recusar"
          aria-label="Recusar"
          @click="recusar(chamada.idLigacao)"
        >
          <i class="ri-phone-fill ligacao-icone--desligar" aria-hidden="true"></i>
          <span class="ligacao-btn-rotulo">Recusar</span>
        </button>
      </div>
      <p v-if="chamada.erro" class="ligacao-erro">{{ chamada.erro }}</p>
    </div>

    <!-- EM ANDAMENTO -->
    <div
      v-for="chamada in ativas"
      :key="chamada.idLigacao"
      class="ligacao-card"
      :class="[
        emEspera[chamada.idLigacao] ? 'ligacao-card--espera' : 'ligacao-card--ativa',
        { 'ligacao-card--solo': solo },
      ]"
    >
      <div
        class="ligacao-topo"
        :title="isMobileViewport ? null : 'Arraste para mover — duplo clique volta ao canto'"
      >
        <img
          v-if="chamada.imagem"
          :src="chamada.imagem"
          class="ligacao-avatar"
          alt=""
          draggable="false"
          @error="chamada.imagem = null"
        />
        <span v-else class="ligacao-avatar ligacao-avatar--vazio" aria-hidden="true">
          {{ iniciais(chamada) }}
        </span>
        <div class="ligacao-info">
          <span class="ligacao-titulo">
            {{ emEspera[chamada.idLigacao] ? 'Em espera' : rotuloEstado(chamada) }}
          </span>
        <!-- Ligação de saída ainda tocando: o cliente não atendeu, então não há
             o que ouvir. Sem esta linha o card dizia "Conectando…" e parecia um
             defeito de áudio. -->
          <small v-if="ehChamandoAgora(chamada)" class="ligacao-aviso">
            Aguardando o cliente atender.
          </small>
        <!-- Cronômetro enquanto fala, duração final quando encerra. Formato de
             largura fixa de propósito: no falado ("59s" → "1m") o card pularia
             de largura a cada minuto. -->
          <small v-else-if="tempoDeFala(chamada)" class="ligacao-duracao">
            {{ tempoDeFala(chamada) }}
          </small>
          <strong class="ligacao-nome">{{ chamada.nome || chamada.codigo }}</strong>
          <small v-if="nomeDaInbox(chamada)" class="ligacao-inbox">
            {{ chamada.direcao === 'REALIZADA' ? 'por' : 'para' }} {{ nomeDaInbox(chamada) }}
          </small>
          <!-- Medidor em toda ativa: as em espera têm o mic suspenso. -->
          <!-- Ligação viva SEM áudio nesta aba: o card continua (é o único
               lugar com o botão de encerrar), mas não pode fingir que o
               microfone está aberto. -->
          <small v-if="!temAudio(chamada.idLigacao)" class="ligacao-aviso">
            Áudio não está nesta aba.
          </small>
          <!-- O operador precisa saber que está falando sozinho. No iOS o
               microfone não funciona com o app em segundo plano nem com a tela
               bloqueada — é limite do sistema, não falha da ligação. -->
          <small v-else-if="chamada.audio === 'pausado'" class="ligacao-aviso ligacao-aviso--forte">
            Volte ao app e mantenha a tela ligada para voltar a falar.
          </small>
          <div
            v-if="temAudio(chamada.idLigacao) && !emEspera[chamada.idLigacao] && chamada.audio !== 'pausado'"
            class="ligacao-medidor"
            aria-hidden="true"
          >
            <div class="ligacao-medidor-nivel" :style="{ width: larguraNivel }"></div>
          </div>
        </div>
      </div>
      <div class="ligacao-acoes">
        <template v-if="temAudio(chamada.idLigacao)">
        <button
          v-if="emEspera[chamada.idLigacao]"
          class="ligacao-btn"
          title="Retomar"
          aria-label="Retomar ligação"
          @click="focar(chamada.idLigacao)"
        >
          <i class="ri-play-fill" aria-hidden="true"></i>
          <span class="ligacao-btn-rotulo">Retomar</span>
        </button>
        <template v-else>
          <button
            class="ligacao-btn"
            :class="{ 'ligacao-btn--ligado': mutados[chamada.idLigacao] }"
            :title="mutados[chamada.idLigacao] ? 'Reativar microfone' : 'Mutar'"
            :aria-label="mutados[chamada.idLigacao] ? 'Reativar microfone' : 'Mutar microfone'"
            @click="alternarMute(chamada.idLigacao)"
          >
            <i :class="mutados[chamada.idLigacao] ? 'ri-mic-off-line' : 'ri-mic-line'" aria-hidden="true"></i>
            <span class="ligacao-btn-rotulo">
              {{ mutados[chamada.idLigacao] ? 'Reativar microfone' : 'Mutar' }}
            </span>
          </button>
          <!-- Espera MANUAL: com várias ativas é o único jeito de isolar uma
               ligação sem encerrá-la. Só aparece quando há mais de uma. -->
          <button
            v-if="comAudio > 1"
            class="ligacao-btn"
            title="Espera"
            aria-label="Colocar em espera"
            @click="porEmEspera(chamada.idLigacao, true)"
          >
            <i class="ri-pause-line" aria-hidden="true"></i>
            <span class="ligacao-btn-rotulo">Espera</span>
          </button>
        </template>
        </template>
        <button
          class="ligacao-btn ligacao-btn--recusar"
          title="Encerrar"
          aria-label="Encerrar ligação"
          @click="encerrar(chamada.idLigacao)"
        >
          <i class="ri-phone-fill ligacao-icone--desligar" aria-hidden="true"></i>
          <span class="ligacao-btn-rotulo">Encerrar</span>
        </button>
      </div>
      <p v-if="chamada.erro" class="ligacao-erro">{{ chamada.erro }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState, mapActions } from 'pinia';
import { useLigacoesStore, type Ligacao } from '@/stores/app/ligacoesStore';
import { useAuthStoreUser } from '@/stores/app/authUser';
import { useDevice } from '@/composables/useDevice';
import { formatarCronometro } from '@/utils/duracao';

/** Posição é preferência do POSTO de trabalho (monitor, layout), não da conta:
 *  fica no localStorage e não no perfil do usuário. */
const CHAVE_POSICAO = 'whiz_ligacao_overlay_pos';
/** Folga mínima entre o card e a borda da janela. */
const MARGEM = 8;
/**
 * Quanto o ponteiro precisa andar para o gesto virar arraste.
 *
 * Sem limiar, um clique parado no card já o converteria de ancorado-à-direita
 * para coordenada fixa — e o `pointerup` SALVARIA isso. Quem nunca quis mover
 * nada ficaria com o card preso no lugar de uma janela antiga.
 */
const LIMIAR_ARRASTE = 3;

const ROTULOS: Record<string, string> = {
  conectando: 'Conectando o áudio...',
  ativo: 'Em ligação',
  encerrado: 'Áudio encerrado',
  erro: 'Falha no áudio',
  // A ligação está de pé; o que parou foi o som (app em segundo plano, tela
  // bloqueada, chamada nativa por cima). Ver `LigacaoAudioEstado`.
  pausado: 'Áudio pausado',
};

export default defineComponent({
  name: 'LigacaoOverlay',
  setup() {
    // `isMobileViewport` e não `isMobileDevice`: o que muda aqui é LAYOUT, e o
    // corte é o mesmo do CSS (991.98px). Desktop com janela estreita cai no
    // modo tela cheia de propósito — o card flutuante não cabe lá também.
    const { isMobileViewport } = useDevice();
    return { isMobileViewport };
  },
  data() {
    return {
      /**
       * "Agora" em ms, avançado de segundo em segundo. O cronômetro é derivado
       * de `dataAtendimento` e deste relógio, e NÃO de um contador próprio: a
       * âncora vem do servidor, então recarregar a página no meio da ligação
       * mostra o tempo real em vez de reiniciar do zero.
       */
      agora: Date.now(),
      relogio: null as ReturnType<typeof setInterval> | null,
      /**
       * Posição escolhida pelo operador, em pixels da viewport (canto superior
       * esquerdo do overlay). `null` = ainda no lugar padrão (topo à direita),
       * que é ancorado pela DIREITA e portanto acompanha o redimensionamento da
       * janela sozinho — só passamos para `left/top` quando alguém arrasta.
       */
      posicao: null as { x: number; y: number } | null,
      /** Gesto já passou do limiar — antes disso ainda é um clique. */
      arrastando: false,
      /**
       * Ponteiro dono do gesto. Segundo dedo na tela não rouba o arraste do
       * primeiro, e `pointerup`/`pointermove` de outro ponteiro são ignorados.
       */
      ponteiro: null as number | null,
      /** Onde o ponteiro desceu, para medir o limiar. */
      origem: { x: 0, y: 0 },
      /** Fim do último arraste, para o duplo clique não confundir os dois. */
      ultimoArrasteEm: 0,
      /** Ponto agarrado DENTRO do card, para ele não saltar para o cursor. */
      pegada: { x: 0, y: 0 },
      /**
       * Posição de ANTES do gesto. Serve para desfazer um arraste que atravessou
       * os 992px no meio: largar o gesto sem salvar não basta, porque `posicao`
       * já foi remexida pelos `arrastar` anteriores e reapareceria ao voltar
       * para a tela larga.
       */
      posicaoAoIniciar: null as { x: number; y: number } | null,
      /**
       * Tamanho medido no início do arraste. Medir a cada movimento se
       * realimentaria: o `max-height` do overlay depende do `top`, então mover
       * para baixo encolheria a pilha, que mudaria o limite, que mudaria o top.
       */
      tamanhoArraste: { largura: 0, altura: 0 },
      /**
       * Altura da janela, em estado, para o `max-height` recalcular no resize.
       * `window.innerHeight` e não `100vh`: no celular o `100vh` é a viewport
       * GRANDE (barra de endereço escondida) e o limite usa `innerHeight` — as
       * duas contas precisam falar da mesma tela.
       */
      alturaJanela: typeof window === 'undefined' ? 0 : window.innerHeight,
      aoRedimensionar: null as (() => void) | null,
      /**
       * Tela cheia recolhida na faixa do topo (mobile). Só estado de VISTA: a
       * ligação, o áudio e o microfone seguem exatamente como estavam.
       */
      minimizada: false,
    };
  },
  computed: {
    ...mapState(useLigacoesStore, [
      'tocando', 'ativas', 'emFoco', 'mutados', 'emEspera', 'nivelMic', 'temAudio',
    ]),
    /**
     * Enquanto ninguém arrastou, nenhum estilo inline: o canto superior direito
     * do CSS continua valendo, inclusive ao redimensionar a janela.
     */
    estiloOverlay(): Record<string, string> {
      // No mobile o overlay é tela cheia: a posição salva (de um desktop, ou de
      // antes da rotação) prenderia o card num canto de uma janela que não
      // existe mais.
      if (this.isMobileViewport || !this.posicao) return {};
      return {
        top: `${this.posicao.y}px`,
        left: `${this.posicao.x}px`,
        // `right: auto` vem da classe `--movida`, não daqui.
        // O `max-height` do CSS assume o card colado no topo; movido, a sobra
        // até o rodapé da janela é outra. Nunca colapsa: `limitar` garante que
        // `y` deixa pelo menos a altura visível do card abaixo dele.
        maxHeight: `${Math.max(0, this.alturaJanela - this.posicao.y - MARGEM)}px`,
      };
    },
    /** Só para observar a pilha crescer/encolher e reencaixar o card. */
    quantidadeDeCards(): number {
      return this.tocando.length + this.ativas.length;
    },
    /**
     * Uma única ligação na pilha. No mobile é o caso que vira tela cheia com
     * avatar grande; com duas ou mais os cards viram linhas compactas, porque
     * "qual delas é esta" passa a valer mais que o tamanho do avatar.
     */
    solo(): boolean {
      return this.quantidadeDeCards === 1;
    },
    /** Texto da faixa minimizada. */
    rotuloBarra(): string {
      // "toque para ABRIR", não "para atender": o clique da faixa só restaura a
      // tela cheia. Prometer o atendimento aqui gasta o toque do operador no
      // pior momento possível — com o telefone tocando.
      if (this.tocando.length) return 'Ligação recebida — toque para abrir';
      // O contador não é enfeite: ofertas concorrentes são o normal deste
      // gateway, e a pílula mostra o cronômetro de UMA só. Sem o número, duas
      // ligações vivas leem como uma.
      const total = this.ativas.length;
      return total > 1 ? `${total} ligações em andamento` : 'Ligação em andamento';
    },
    /**
     * Cronômetro na faixa: o da ligação que está FALANDO (fora de espera), que
     * é a que o operador quer acompanhar de canto de olho.
     */
    tempoBarra(): string {
      const emFala = this.ativas.find(c => !this.emEspera[c.idLigacao]) ?? this.ativas[0];
      return emFala ? this.tempoDeFala(emFala) : '';
    },
    /**
     * Quantas ligações têm sessão de áudio nesta aba. É o número que descreve
     * "as vozes vão se misturar" e que decide se pôr uma em espera isola alguma
     * coisa — `ativas` inclui ligação viva sem áudio nenhum.
     */
    comAudio(): number {
      return this.ativas.filter(c => this.temAudio(c.idLigacao)).length;
    },
    /** Ganho de 140% no medidor: fala normal fica bem abaixo do fundo de escala. */
    larguraNivel(): string {
      return `${Math.min(100, this.nivelMic * 140)}%`;
    },
  },
  watch: {
    /**
     * O overlay só existe no DOM enquanto há ligação (`v-if`), então a posição
     * salva só pode ser conferida contra a janela depois que ele monta. E a
     * altura muda a cada oferta que chega — a pilha pode passar do rodapé.
     */
    quantidadeDeCards(quantidade: number) {
      // Pilha esvaziou no meio do gesto (a ligação que você estava arrastando
      // para fora do caminho foi cancelada): o `v-if` arranca o elemento e o
      // `pointerup` nunca chega. Sem zerar aqui, a PRÓXIMA ligação nasceria com
      // o arraste ligado e o card seguiria o cursor sem ninguém pressionar nada.
      if (!quantidade) {
        this.encerrarGesto();
        // Pilha vazia: a próxima ligação nasce em tela cheia, não recolhida.
        this.minimizada = false;
      }
      this.$nextTick(() => this.reencaixar());
    },
    /**
     * Ao voltar do minimizado, reencaixa: enquanto a pílula estava na tela o
     * overlay não existia no DOM, então `reencaixar` saía cedo e a janela pode
     * ter mudado de tamanho no meio (monitor desconectado, janela reduzida). Sem
     * isto o card reaparecia numa coordenada de uma janela que não existe mais,
     * possivelmente fora da tela, levando junto o botão de encerrar.
     */
    minimizada(valor: boolean) {
      if (!valor) this.$nextTick(() => this.reencaixar());
    },
    /**
     * Oferta nova reabre a tela cheia. Uma chamada tocando escondida atrás da
     * faixa é uma chamada perdida — e ela chega justamente quando o operador
     * está olhando outra coisa.
     */
    'tocando.length'(quantidade: number, anterior: number) {
      // Só quando SOBE. Com `if (quantidade)`, duas ligações tocando e uma
      // sendo recusada (2 → 1) reabria a tela cheia — o overlay saltava na cara
      // do operador por uma ligação ACABANDO, no meio do que ele estava fazendo.
      if (quantidade > (anterior ?? 0)) this.minimizada = false;
    },
  },
  methods: {
    ...mapActions(useLigacoesStore, [
      'atender',
      'recusar',
      'encerrar',
      'alternarMute',
      'focar',
      'porEmEspera',
      'pararAudio',
    ]),
    /**
     * Arrasta o overlay INTEIRO (a pilha), não um card: o WhatsApp entrega
     * ofertas concorrentes e a lista cresce sozinha — cards com posição própria
     * se sobreporiam sem ninguém ter arrastado nada.
     */
    iniciarArraste(evento: PointerEvent) {
      // Um gesto por vez: segundo dedo não sequestra a pegada do primeiro.
      if (this.ponteiro !== null) return;
      // Tela cheia não se arrasta: não há canto para onde ir, e a captura de
      // ponteiro no topo só disputaria o toque com o resto do card.
      if (this.isMobileViewport) return;
      if (evento.pointerType === 'mouse' && evento.button !== 0) return;
      // Só o topo (avatar + textos) é punho. Os botões ficam de fora de
      // propósito: "Atender"/"Recusar" são clique de um toque só, e um arraste
      // acidental sobre eles engoliria o clique.
      if (!(evento.target as HTMLElement | null)?.closest('.ligacao-topo')) return;

      const el = this.$refs.overlay as HTMLElement | undefined;
      if (!el) return;

      const caixa = el.getBoundingClientRect();
      // Medido agora, APLICADO só depois do limiar: enquanto o gesto for um
      // clique parado, o card segue ancorado à direita pelo CSS. Converter já
      // no `pointerdown` é o que fazia um toque qualquer congelar a posição.
      this.origem = { x: evento.clientX, y: evento.clientY };
      this.pegada = { x: evento.clientX - caixa.left, y: evento.clientY - caixa.top };
      this.tamanhoArraste = this.medir(el);
      this.posicaoAoIniciar = this.posicao ? { ...this.posicao } : null;
      this.ponteiro = evento.pointerId;

      // Captura: o ponteiro pode sair do card (ou da janela) no meio do gesto e
      // o `pointerup` ainda chega aqui, em vez de deixar o card grudado.
      el.setPointerCapture(evento.pointerId);
      // Sem `preventDefault()` aqui de propósito: cancelar o `pointerdown`
      // suprime os eventos de mouse de compatibilidade e põe o `dblclick` (a
      // volta ao canto) em risco. Quem impede a seleção de texto é o
      // `user-select: none` do punho no CSS.
    },
    arrastar(evento: PointerEvent) {
      if (this.ponteiro !== evento.pointerId) return;
      // A janela encolheu para baixo de 992px NO MEIO do arraste (girar o
      // aparelho, dividir a tela, encaixar a janela). Larga o gesto sem salvar:
      // a coordenada seria medida contra uma janela em que ninguém arrastou
      // nada, e `salvarPosicao` a gravaria como preferência.
      if (this.isMobileViewport) {
        this.abandonarGesto();
        return;
      }

      // Botão já solto e nós não vimos o `pointerup` (soltou fora da janela,
      // ou o DevTools comeu o evento): encerra em vez de seguir o cursor.
      if (evento.pointerType === 'mouse' && evento.buttons === 0) {
        // Encerra SALVANDO: por este caminho o card já foi movido de verdade, e
        // descartar a posição faria o arraste "não pegar" depois do recarregar.
        this.finalizarGesto(this.arrastando);
        return;
      }

      if (!this.arrastando) {
        const distancia = Math.hypot(evento.clientX - this.origem.x, evento.clientY - this.origem.y);
        if (distancia < LIMIAR_ARRASTE) return;
        this.arrastando = true;
      }

      this.posicao = this.limitar(
        evento.clientX - this.pegada.x,
        evento.clientY - this.pegada.y,
        this.tamanhoArraste,
      );
    },
    soltarArraste(evento: PointerEvent) {
      if (this.ponteiro !== evento.pointerId) return;
      // Mesma guarda de `arrastar`, e ela é obrigatória aqui: com o ponteiro
      // PARADO no instante em que a janela cruza os 992px, `arrastar` não roda
      // mais nenhuma vez e nunca abandona o gesto — o `pointerup` chegaria com
      // `arrastando` ainda true e `salvarPosicao` gravaria a coordenada. Pior:
      // `limitar` mede contra a janela VIVA, então o último movimento antes do
      // corte já grudou o card na margem, e era esse 8 que iria para o disco.
      if (this.isMobileViewport) {
        const el = this.$refs.overlay as HTMLElement | undefined;
        if (el?.hasPointerCapture(evento.pointerId)) el.releasePointerCapture(evento.pointerId);
        this.abandonarGesto();
        return;
      }
      // Lido ANTES de soltar a captura: `releasePointerCapture` dispara
      // `lostpointercapture` de forma síncrona, e o handler dele zera o gesto.
      const moveu = this.arrastando;
      const el = this.$refs.overlay as HTMLElement | undefined;
      if (el?.hasPointerCapture(evento.pointerId)) el.releasePointerCapture(evento.pointerId);
      this.finalizarGesto(moveu);
    },
    /**
     * Fecha o gesto. Clique sem arraste não salva nada: quem não moveu continua
     * com o card ancorado à direita, acompanhando o resize da janela.
     */
    finalizarGesto(moveu: boolean) {
      this.encerrarGesto();
      if (!moveu) return;
      this.ultimoArrasteEm = Date.now();
      this.salvarPosicao();
    },
    /**
     * Desiste do gesto e DESFAZ o que ele já tinha mexido. Diferente de
     * `encerrarGesto`, que só zera o estado do ponteiro: aqui a posição volta
     * ao que era antes de o dedo descer.
     */
    abandonarGesto() {
      this.posicao = this.posicaoAoIniciar;
      this.encerrarGesto();
    },
    /** Zera só o estado do gesto. Idempotente — vem de mais de um caminho. */
    encerrarGesto() {
      this.arrastando = false;
      this.ponteiro = null;
    },
    /**
     * Duplo clique no punho devolve o card ao canto padrão. É a saída para
     * quem largou o card num lugar ruim e para quem quer a ancoragem à direita
     * de volta — sem isso, "mover" seria uma porta de mão única.
     */
    restaurarPosicao(evento: MouseEvent) {
      // Em tela cheia não existe "voltar ao canto" — e o duplo toque aqui não é
      // um gesto do nosso card: `index.html` não desliga o zoom, então o
      // duplo-toque-para-ampliar do celular chega como `dblclick` e apagaria a
      // posição que o operador ajustou NOUTRA tela. O freio dos 400 ms abaixo
      // não protege: `ultimoArrasteEm` nunca sai de 0 no mobile, porque arraste
      // nenhum começa.
      if (this.isMobileViewport) return;
      // Guard pela NEGATIVA (não é botão), e não por "é o punho": com pointer
      // capture ativo o Chrome retarget o `click`/`dblclick` para o elemento
      // que capturou — o próprio overlay —, então exigir `.ligacao-topo` no
      // alvo nunca passava e o duplo clique não fazia nada. Verificado no
      // Chrome 2026-09-11: `dblclick.target` = `.ligacao-overlay`.
      if ((evento.target as HTMLElement | null)?.closest('.ligacao-acoes')) return;
      // Dois arrastes curtos e rápidos no mesmo ponto também viram `dblclick`
      // no navegador — e jogar o card de volta ao canto bem na hora em que a
      // pessoa acabou de posicioná-lo seria o oposto do pedido.
      if (Date.now() - this.ultimoArrasteEm < 400) return;
      this.posicao = null;
      try {
        localStorage.removeItem(CHAVE_POSICAO);
      } catch {
        // Sem localStorage a posição já não persistia; nada a desfazer.
      }
    },
    /**
     * Mantém o card inteiro dentro da janela. Não é estética: fora da tela o
     * "Atender" fica inalcançável e a ligação se perde — e como a posição é
     * salva, uma vez fora seria fora para sempre.
     */
    limitar(x: number, y: number, tamanho: { largura: number; altura: number }) {
      const maxX = window.innerWidth - tamanho.largura - MARGEM;
      const maxY = window.innerHeight - tamanho.altura - MARGEM;
      // `tamanho.altura` vem de `medir`, já limitada à janela — sem isso o
      // limite e o `max-height` se realimentavam: descer o card encolhia a
      // pilha, o que liberava descer mais, até a altura chegar a ZERO e o card
      // sumir da tela sem nunca sair dos limites.
      return {
        // Janela menor que o card (celular deitado, pilha alta): encosta na
        // margem em vez de inverter o intervalo e jogar o card para fora.
        x: Math.round(maxX < MARGEM ? MARGEM : Math.min(Math.max(x, MARGEM), maxX)),
        y: Math.round(maxY < MARGEM ? MARGEM : Math.min(Math.max(y, MARGEM), maxY)),
      };
    },
    /**
     * Tamanho do overlay para efeito de limite.
     *
     * A altura é a NATURAL (`scrollHeight`), não a que está na tela: a que está
     * na tela já foi cortada pelo `max-height`, que por sua vez depende da
     * posição — medir a cortada é o que fazia o card encolher até desaparecer.
     * E o resultado é limitado à janela, senão uma pilha alta não teria posição
     * válida nenhuma.
     */
    medir(el: HTMLElement) {
      const caixa = el.getBoundingClientRect();
      const natural = Math.max(caixa.height, el.scrollHeight);
      return {
        largura: caixa.width,
        altura: Math.min(natural, window.innerHeight - MARGEM * 2),
      };
    },
    /** Reaplica o limite quando a janela muda de tamanho ou a pilha cresce. */
    reencaixar() {
      this.alturaJanela = window.innerHeight;
      // No mobile o overlay ocupa a tela inteira, então `medir` devolve a
      // largura da janela e `limitar` calcula `maxX = innerWidth - innerWidth -
      // MARGEM`, sempre negativo — o resultado é SEMPRE {MARGEM, MARGEM}.
      // Nada aparece na hora (`estiloOverlay` devolve {} aqui), mas a posição
      // salva do desktop já foi destruída: ao voltar para a tela larga o card
      // ressurge grudado no canto superior esquerdo. A posição é preferência do
      // posto de trabalho — o mobile não opina sobre ela.
      if (this.isMobileViewport || !this.posicao) return;
      const el = this.$refs.overlay as HTMLElement | undefined;
      if (!el) return;
      this.posicao = this.limitar(this.posicao.x, this.posicao.y, this.medir(el));
    },
    salvarPosicao() {
      try {
        localStorage.setItem(CHAVE_POSICAO, JSON.stringify(this.posicao));
      } catch {
        // Aba anônima ou cota estourada: a posição é conforto, não pode
        // atrapalhar a ligação.
      }
    },
    carregarPosicao() {
      try {
        const cru = localStorage.getItem(CHAVE_POSICAO);
        if (!cru) return;
        const salvo = JSON.parse(cru);
        if (Number.isFinite(salvo?.x) && Number.isFinite(salvo?.y)) {
          // Ainda NÃO limitado: o overlay pode nem estar no DOM agora. Quem
          // confere contra a janela é o `reencaixar` do watch.
          this.posicao = { x: salvo.x, y: salvo.y };
        }
      } catch {
        // Valor corrompido: volta para o canto padrão.
      }
    },
    /**
     * Recolhe a tela cheia. Encerra qualquer gesto antes: se a janela cruzou os
     * 992px com o ponteiro ainda capturado, o `v-if` arranca o elemento e o
     * `lostpointercapture` nunca chega — `ponteiro` ficaria preso e a primeira
     * guarda de `iniciarArraste` recusaria todo arraste futuro no desktop.
     */
    minimizarTelaCheia() {
      this.encerrarGesto();
      this.minimizada = true;
    },
    rotuloEstado(chamada: Ligacao): string {
      // Saída ainda tocando: o estado do NOSSO socket de áudio não descreve a
      // chamada — ele já está pronto enquanto o telefone do cliente só toca.
      if (this.ehChamandoAgora(chamada)) return 'Chamando…';
      // Sem sessão de áudio nesta aba, `chamada.audio` é `undefined` e o
      // fallback dizia "Conectando o áudio..." para sempre — uma promessa que
      // ninguém vai cumprir, porque não há conexão em curso. A ligação existe e
      // está em pé; é isso que o card deve dizer.
      if (!this.temAudio(chamada.idLigacao)) return 'Em ligação';
      return ROTULOS[chamada.audio ?? 'conectando'] ?? 'Em ligação';
    },
    /** Ligação que ESTE operador discou e que ainda está tocando. */
    ehChamandoAgora(chamada: Ligacao): boolean {
      return chamada.direcao === 'REALIZADA' && chamada.situacao === 'TOCANDO';
    },
    /**
     * Nome da inbox que recebeu (ou originou) a ligação.
     *
     * Resolvido no cliente, a partir das inboxes que o operador já tem em mãos:
     * o evento carrega só o `idInbox`, e mandar o nome junto seria duplicar no
     * payload um dado que o front já conhece e que pode ser renomeado.
     *
     * Vazio quando a inbox não está na lista (conta trocada, inbox recém-criada)
     * — melhor omitir a linha que mostrar um id cru no card.
     */
    nomeDaInbox(chamada: Ligacao): string {
      const inbox = useAuthStoreUser().inboxesFull.find(i => i.id === chamada.idInbox);
      return inbox?.nome ?? '';
    },

    /**
     * Iniciais para o avatar sem foto. Cai no telefone quando não há nome —
     * ligação de número desconhecido é exatamente o caso que a feature mostra.
     */
    iniciais(chamada: Ligacao): string {
      const nome = (chamada.nome ?? '').trim();
      if (!nome) return (chamada.codigo ?? '').slice(-2);
      const partes = nome.split(/\s+/).filter(Boolean);
      const primeira = partes[0]?.[0] ?? '';
      const ultima = partes.length > 1 ? (partes[partes.length - 1][0] ?? '') : '';
      return (primeira + ultima).toUpperCase();
    },

    /**
     * Cronômetro enquanto a ligação corre; duração final depois de encerrada.
     *
     * Vazio quando não houve fala (perdida/recusada): "00:00" alegaria uma
     * conversa de zero segundo, quando o caso é que ninguém atendeu.
     */
    tempoDeFala(chamada: Ligacao): string {
      if (chamada.duracaoSegundos !== null && chamada.duracaoSegundos !== undefined) {
        return formatarCronometro(chamada.duracaoSegundos);
      }
      if (!chamada.dataAtendimento) return '';

      const inicio = new Date(chamada.dataAtendimento).getTime();
      if (!Number.isFinite(inicio)) return '';
      return formatarCronometro((this.agora - inicio) / 1000);
    },
  },
  mounted() {
    // 1 s é a menor granularidade que o cronômetro mostra; um timer mais rápido
    // só re-renderizaria o overlay sem mudar um pixel.
    this.relogio = setInterval(() => { this.agora = Date.now(); }, 1000);
    this.carregarPosicao();
    // Recarregar a página no meio de uma ligação já monta com card na tela: o
    // watch não dispara para o valor inicial, então o primeiro encaixe é aqui.
    this.$nextTick(() => this.reencaixar());
    // Monitor desconectado / janela reduzida: sem isto o card salvo em 2560px
    // ficaria fora da tela de 1366px, com o "Atender" inalcançável.
    this.aoRedimensionar = () => this.reencaixar();
    window.addEventListener('resize', this.aoRedimensionar);
  },
  beforeUnmount() {
    if (this.relogio) clearInterval(this.relogio);
    if (this.aoRedimensionar) window.removeEventListener('resize', this.aoRedimensionar);
    // Solta o microfone se a aba navegar para fora com ligação viva — sem isto o
    // browser segue mostrando "esta aba está usando seu microfone".
    this.pararAudio();
  },
});
</script>

<style scoped>
.ligacao-overlay {
  position: fixed;
  top: 1rem;
  right: 1rem;
  /* Acima dos 10000 de `AllNotificationsModal` e `NewsModal`: um modal de avisos
     por cima de uma ligação tocando esconde a única coisa com prazo na tela.
     O 100000 do SweetAlert fica de fora de propósito — é diálogo de confirmação,
     e tirá-lo da frente pediria mexer em outro componente. */
  z-index: 10050;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  /* Com várias ligações a pilha pode passar da tela. */
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
}

/* Arrastado: `top`/`left` vêm do estilo inline; o `right` do padrão precisa sair
   do caminho, senão o card fica esticado entre os dois lados. */
.ligacao-overlay--movida { right: auto; }
/* Cores do tema, não fixas: o card ficava BRANCO no tema escuro porque
   `--surface-card`/`--text-color` são vocabulário do PrimeVue e não existem
   aqui — toda regra caía no fallback claro. O app troca de tema pelo
   `data-bs-theme` do body (App.vue `updateDOM`), e as variáveis do Bootstrap
   5.3 acompanham sozinhas; herdadas do body, valem dentro do estilo scoped. */
.ligacao-card {
  min-width: 17rem;
  max-width: 22rem;
  padding: 1rem;
  border-radius: 0.75rem;
  background: var(--bs-body-bg);
  color: var(--bs-body-color);
  box-shadow: 0 0.5rem 1.5rem rgb(0 0 0 / 25%);
  border: 1px solid var(--bs-border-color);
}

.ligacao-card--ativa { border-color: var(--bs-success); }
/* Em espera fica visivelmente recuada: a diferença tem de ser óbvia. */
.ligacao-card--espera { border-color: var(--bs-warning); opacity: 0.75; }

/* Avatar e texto lado a lado; o texto pode encolher, o avatar não.
   É também o PUNHO do arraste: `touch-action: none` só aqui, para a pilha
   continuar rolando ao toque quando há mais ligações do que cabe na tela. */
.ligacao-topo {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  cursor: grab;
  touch-action: none;
  /* Arrastar e selecionar texto são o mesmo gesto no mesmo alvo; o punho abre
     mão da seleção. O card some quando a ligação acaba, então copiar o número
     daqui nunca foi caminho confiável. */
  user-select: none;
}
.ligacao-overlay--arrastando .ligacao-topo { cursor: grabbing; }

.ligacao-avatar {
  flex: 0 0 auto;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--bs-border-color);
}

/* Sem foto: iniciais sobre a superfície neutra do tema. */
.ligacao-avatar--vazio {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bs-secondary-bg);
  color: var(--bs-secondary-color);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

/* `min-width: 0` deixa o nome longo truncar em vez de esticar o card. */
.ligacao-info { display: flex; flex-direction: column; gap: 0.125rem; min-width: 0; }

.ligacao-inbox {
  font-size: 0.7rem;
  opacity: 0.65;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ligacao-titulo { font-size: 0.75rem; opacity: 0.7; }
.ligacao-nome { font-size: 1rem; }
.ligacao-codigo { font-size: 0.75rem; opacity: 0.6; }
.ligacao-aviso { font-size: 0.7rem; opacity: 0.6; }
/* Aviso que muda o que a pessoa deve FAZER agora não pode ficar em 60% de
   opacidade junto com os secundários. */
.ligacao-aviso--forte { opacity: 1; color: var(--bs-warning-text-emphasis); font-weight: 600; }

.ligacao-duracao {
  /* Tabular para o número não dançar a cada segundo: os dígitos passam a ter
     largura igual, então "11" e "18" ocupam o mesmo espaço. */
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
}

.ligacao-medidor {
  height: 0.25rem;
  margin-top: 0.5rem;
  border-radius: 999px;
  background: var(--bs-secondary-bg);
  overflow: hidden;
}
.ligacao-medidor-nivel {
  height: 100%;
  background: var(--bs-success);
  transition: width 80ms linear;
}

.ligacao-acoes { display: flex; gap: 0.5rem; margin-top: 0.75rem; }

.ligacao-btn {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 0.8rem;
}
.ligacao-btn:disabled { opacity: 0.5; cursor: not-allowed; }
/* Atender/recusar ficam SÓLIDOS nos dois temas: são ação semântica (verde =
   atender, vermelho = recusar) e o branco por cima é legível nas duas versões
   da cor. O que muda com o tema é a superfície do card, não o significado. */
.ligacao-btn--atender { background: var(--bs-success); border-color: var(--bs-success); color: #fff; }
.ligacao-btn--recusar { background: var(--bs-danger); border-color: var(--bs-danger); color: #fff; }

/* `-text-emphasis` e não `--bs-danger`: o vermelho puro sobre fundo escuro tem
   contraste ruim, e o Bootstrap já define a variante legível por tema. */
.ligacao-erro { margin: 0.5rem 0 0; font-size: 0.72rem; color: var(--bs-danger-text-emphasis); }

/* Ícone só existe no modo mobile; no desktop o botão continua sendo texto. */
.ligacao-btn i { display: none; }
/* Desligar = o mesmo telefone virado, como em todo telefone desde 2010.
   Remixicon não traz `phone-off`, e girar o ícone que existe é mais barato que
   arrastar um SVG solto para dentro do componente. */
.ligacao-icone--desligar { transform: rotate(135deg); }

/* ==========================================================================
   MOBILE — tela cheia, no formato do WhatsApp

   Abaixo de 992px (mesmo corte de `useDevice` e das classes `d-lg-*`) o card
   flutuante não serve: 17rem de largura mínima já tomam a tela inteira, o
   arraste disputa o toque com os botões e alvos de 0.8rem de texto ficam no
   limite do que um polegar acerta. Então o overlay vira TELA: avatar grande,
   nome centralizado e botões redondos no rodapé.

   Tudo aqui é pele — nenhum estado, nenhuma ação muda. A mesma marcação serve
   aos dois layouts.
   ========================================================================== */
.ligacao-overlay--mobile {
  inset: 0;
  max-height: none;
  gap: 0;
  background: var(--bs-body-bg);
  /* Sem isto o deslize vertical sobre a tela de ligação vaza para a lista de
     conversas atrás dela, que perde a posição de rolagem sem ninguém ver. */
  overscroll-behavior: contain;
}

/* Desktop: pastilha própria acima da pilha, encostada à direita como o card.
   Precisa de superfície: o overlay não tem fundo (só o card tem), então um
   chevron solto flutuava sobre o app sem contraste nenhum, parecendo artefato
   de renderização em vez de controle. */
.ligacao-cabecalho {
  display: flex;
  justify-content: flex-end;
}
.ligacao-cabecalho .ligacao-minimizar {
  border-radius: 999px;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  box-shadow: 0 0.25rem 0.75rem rgb(0 0 0 / 20%);
}
.ligacao-minimizar {
  padding: 0.25rem 0.5rem;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0.75;
}
.ligacao-minimizar:hover { opacity: 1; }

/* Mobile: vira o topo da tela cheia. `sticky` porque com três ligações a pilha
   rola, e o cabeçalho ia junto — levando embora o único jeito de sair da tela
   cheia sem desligar alguém. */
.ligacao-overlay--mobile .ligacao-cabecalho {
  position: sticky;
  top: 0;
  z-index: 1;
  justify-content: flex-start;
  padding: 0.25rem 0.5rem;
  background: var(--bs-body-bg);
}
.ligacao-overlay--mobile .ligacao-minimizar { font-size: 1.75rem; opacity: 1; }

/* Sem moldura: em tela cheia a borda e a sombra do card desenhariam uma caixa
   dentro da própria tela. */
.ligacao-overlay--mobile .ligacao-card {
  width: 100%;
  min-width: 0;
  max-width: none;
  padding: 0.75rem 1.25rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
/* Com mais de uma ligação os cards viram linhas; a divisória diz onde uma
   acaba e a outra começa, já que não há mais borda. */
.ligacao-overlay--mobile .ligacao-card + .ligacao-card {
  border-top: 1px solid var(--bs-border-color);
}

/* Ligação única: ocupa a tela e empurra os botões para o rodapé, ao alcance do
   polegar — é lá que a mão já está. */
/* Identidade no terço superior, controles no rodapé — o polegar alcança os
   botões sem reposicionar a mão. `justify-content: center` deixava o bloco
   inteiro boiando no meio com meia tela vazia embaixo (visto na captura). */
.ligacao-overlay--mobile .ligacao-card--solo {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-top: 10vh;
  padding-bottom: 2.5rem;
}
.ligacao-overlay--mobile .ligacao-card--solo .ligacao-acoes { margin-top: auto; }

.ligacao-overlay--mobile .ligacao-topo {
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
  /* Sem arraste no mobile: o punho volta a ser conteúdo comum, e a rolagem da
     pilha volta a funcionar no toque. */
  cursor: default;
  touch-action: auto;
}
.ligacao-overlay--mobile .ligacao-info { align-items: center; max-width: 100%; }

.ligacao-overlay--mobile .ligacao-card--solo .ligacao-avatar {
  width: 8.5rem;
  height: 8.5rem;
  font-size: 2.25rem;
}
.ligacao-overlay--mobile .ligacao-card--solo .ligacao-nome { font-size: 1.6rem; font-weight: 600; }
.ligacao-overlay--mobile .ligacao-card--solo .ligacao-titulo,
.ligacao-overlay--mobile .ligacao-card--solo .ligacao-duracao { font-size: 0.95rem; }
.ligacao-overlay--mobile .ligacao-card--solo .ligacao-codigo,
.ligacao-overlay--mobile .ligacao-card--solo .ligacao-inbox,
.ligacao-overlay--mobile .ligacao-card--solo .ligacao-aviso { font-size: 0.8rem; }
.ligacao-overlay--mobile .ligacao-medidor { width: 8rem; }

/* Anel pulsando enquanto toca: é como o WhatsApp diz "ainda está chamando" sem
   escrever nada — e o card solo não tem mais nada em movimento. */
@keyframes ligacao-pulso {
  0% { box-shadow: 0 0 0 0 rgba(var(--bs-success-rgb), 0.45); }
  100% { box-shadow: 0 0 0 1.75rem rgba(var(--bs-success-rgb), 0); }
}
.ligacao-overlay--mobile .ligacao-card--tocando.ligacao-card--solo .ligacao-avatar {
  animation: ligacao-pulso 1.6s ease-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .ligacao-overlay--mobile .ligacao-card--tocando.ligacao-card--solo .ligacao-avatar {
    animation: none;
  }
}

/* Botões redondos com ícone. O texto sai do desenho mas continua no
   `aria-label`/`title` de cada botão. */
.ligacao-overlay--mobile .ligacao-acoes {
  justify-content: center;
  gap: 1.25rem;
  margin-top: 1.5rem;
}
.ligacao-overlay--mobile .ligacao-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  padding: 0;
  border-color: transparent;
  border-radius: 50%;
  background: var(--bs-secondary-bg);
  font-size: 1.35rem;
}
.ligacao-overlay--mobile .ligacao-card--solo .ligacao-btn {
  width: 4.25rem;
  height: 4.25rem;
  font-size: 1.75rem;
}
/* Atender/recusar/encerrar PRECISAM reafirmar a cor aqui: a regra genérica
   acima é `.ligacao-overlay--mobile .ligacao-btn` (duas classes) e vence
   `.ligacao-btn--atender` (uma classe) por especificidade, mesmo vindo antes no
   arquivo. Sem isto os três botões saem cinza-claro idênticos — verde e
   vermelho, a única pista de qual é qual num botão redondo SEM rótulo, se
   perdem. Visto na captura headless de 390px; o desktop nunca foi afetado,
   porque lá a regra genérica do mobile não se aplica. */
.ligacao-overlay--mobile .ligacao-btn--atender {
  background: var(--bs-success);
  color: #fff;
}
.ligacao-overlay--mobile .ligacao-btn--recusar {
  background: var(--bs-danger);
  color: #fff;
}
/* Os neutros (microfone, espera, retomar) sumiam contra o fundo da tela cheia:
   `--bs-secondary-bg` é quase o `--bs-body-bg`. A borda devolve a silhueta de
   botão sem inventar uma cor com significado. */
.ligacao-overlay--mobile .ligacao-btn {
  border-color: var(--bs-border-color);
}
.ligacao-overlay--mobile .ligacao-btn i { display: block; }
.ligacao-overlay--mobile .ligacao-btn-rotulo { display: none; }
/* Microfone mutado fica SÓLIDO: num botão redondo sem rótulo, o estado precisa
   estar na cor — o ícone riscado sozinho se confunde com o normal de relance. */
.ligacao-overlay--mobile .ligacao-btn--ligado {
  background: var(--bs-body-color);
  color: var(--bs-body-bg);
}
/* Recusar à esquerda, atender à direita: a ordem do telefone. No desktop a
   leitura é de texto e a ordem natural (ação principal primeiro) continua. */
.ligacao-overlay--mobile .ligacao-card--tocando .ligacao-acoes { flex-direction: row-reverse; }

/* Faixa do modo minimizado: verde, colada no topo, por cima do app — a mesma
   convenção do WhatsApp para "você está em uma ligação". */
/* PÍLULA ABAIXO do cabeçalho, não faixa no topo.

   Empurrar o conteúdo para baixo (padding no body) está fora: o app inteiro se
   apoia em `height: 100vh` / `calc(100vh - Npx)` (app.min.css), e o campo de
   mensagem sairia da tela.

   Centralizar no topo também não resolvia, ao contrário do que esta regra
   afirmava antes: no mobile `.user-chat-topbar` é `position: fixed` com
   `min-height: 73px` (app.min.css:1366), e ali NÃO há só texto — o avatar
   (ChatHeader.vue:13) e o bloco do nome (ChatHeader.vue:22) são alvos de clique
   de `toggleProfile()`, e em telas de 360px a pílula ainda alcançava o botão de
   voltar. Cobrir justamente os controles que a pessoa minimizou a ligação para
   usar é o contrário do objetivo.

   Então ela desce abaixo dos 73px do cabeçalho. Fora do chat (admin, kanban,
   contatos) não existe essa barra fixa e a pílula flutua um pouco mais abaixo,
   sobre conteúdo — nunca sobre chrome. */
.ligacao-barra {
  position: fixed;
  top: 4.75rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10050;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: calc(100% - 1.5rem);
  padding: 0.4rem 0.9rem;
  border: 0;
  border-radius: 999px;
  background: var(--bs-success);
  color: #fff;
  box-shadow: 0 0.25rem 0.75rem rgb(0 0 0 / 30%);
  font-size: 0.8rem;
  text-align: left;
}
.ligacao-barra-texto {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ligacao-barra-tempo { font-variant-numeric: tabular-nums; }

/* No desktop a pílula volta para o canto de onde o card saiu: centralizada no
   topo ela cairia em cima do cabeçalho da conversa, que ali não é fixo e ocupa
   a largura inteira. */
/* No desktop a pílula desce e encosta à esquerda do canto superior direito.
   `top/right: 1rem` era exatamente onde mora o `InboxDisconnectAlert`
   (`position: fixed; top: 16px; right: 16px; z-index: 1080`), e com z-index
   10050 a pílula apagava o alerta de inbox desconectada — além da pilha de
   toasts, que também nasce nesse canto. Minimizar a ligação para desobstruir a
   tela não pode esconder justamente os avisos do canto. */
@media (min-width: 992px) {
  .ligacao-barra {
    top: 1rem;
    right: 23rem;
    left: auto;
    transform: none;
  }
}
</style>
